import { useState, useEffect, useRef, useMemo } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon, SparklesIcon, ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { IUserModel, Resolution, AspectRatioLabels, getLabelsByLocale, Locale } from '@packages/global';
import { useModelStore } from '../../../stores/modelStore';

export interface GenerateStoryboardConfig {
  text2imageModelId: string;
  imageEditModelId: string;
  text2speechModelId: string;
  aspectRatio: string;
  /** 是否生成图片（起始帧/结束帧）。关闭后后端会跳过所有图片生成任务 */
  generateImages: boolean;
  /** 是否生成尾帧（仅当 generateImages 为 true 时生效） */
  generateEndFrame: boolean;
}

interface GenerateStoryboardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (config: GenerateStoryboardConfig) => void;
  /** 是否为重新生成模式 */
  isRegenerate?: boolean;
  /** 初始配置 */
  initialConfig: GenerateStoryboardConfig;
  /** 模型列表 */
  text2imageModels: IUserModel[];
  imageEditModels: IUserModel[];
  text2speechModels: IUserModel[];
  /** 分镜数量 - 用于计算预估剧点 */
  shotCount: number;
  /** 分辨率 - 用于计算预估剧点 */
  resolution?: Resolution;
  /** 是否正在处理 */
  isProcessing?: boolean;
}

/** 内联模型选择器 */
function InlineModelSelector({
  label,
  models,
  value,
  onChange,
}: {
  label: string;
  models: IUserModel[];
  value: string;
  onChange: (id: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const selectedModel = models.find(m => m.id === value);

  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-400">{label}</span>
      </div>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2.5 bg-bg-elevated border border-border-subtle rounded-lg hover:border-border-default transition-colors cursor-pointer"
      >
        <span className="text-sm text-gray-200 truncate">
          {selectedModel?.title || '请选择'}
        </span>
        <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-bg-elevated border border-border-subtle rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto">
          {models.map(model => (
            <button
              key={model.id}
              type="button"
              onClick={() => {
                onChange(model.id);
                setIsOpen(false);
              }}
              className={`w-full flex items-center justify-between px-3 py-2 text-left hover:bg-bg-surface transition-colors cursor-pointer ${model.id === value ? 'bg-bg-surface' : ''
                }`}
            >
              <span className="text-sm text-gray-200 truncate">{model.title}</span>
              {model.id === value && <CheckIcon className="w-4 h-4 text-primary" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** 比例选择器 */
function AspectRatioButtons({
  value,
  onChange,
}: {
  value: string;
  onChange: (ratio: string) => void;
}) {
  const { i18n } = useTranslation();
  const ratios = Object.entries(getLabelsByLocale(AspectRatioLabels, i18n.language as Locale));

  return (
    <div className="flex gap-2 flex-wrap">
      {ratios.map(([ratio, label]) => (
        <button
          key={ratio}
          type="button"
          onClick={() => onChange(ratio)}
          className={`px-3 py-1.5 text-sm rounded-lg border transition-colors cursor-pointer ${value === ratio
            ? 'bg-primary/20 border-primary text-primary'
            : 'bg-bg-elevated border-border-subtle text-gray-300 hover:border-border-default'
            }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/** 开关组件 */
function ToggleSwitch({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="w-full flex items-center justify-between p-3 bg-bg-elevated border border-border-subtle rounded-lg hover:border-border-default transition-colors cursor-pointer"
    >
      <div className="text-left">
        <div className="text-sm text-gray-200">{label}</div>
        {description && <div className="text-xs text-gray-400 mt-0.5">{description}</div>}
      </div>
      <div
        className={`w-10 h-6 rounded-full p-0.5 transition-colors ${checked ? 'bg-primary' : 'bg-border-default'
          }`}
      >
        <div
          className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0'
            }`}
        />
      </div>
    </button>
  );
}

export default function GenerateStoryboardModal({
  isOpen,
  onClose,
  onConfirm,
  isRegenerate = false,
  initialConfig,
  text2imageModels,
  imageEditModels,
  text2speechModels,
  shotCount,
  resolution,
  isProcessing = false,
}: GenerateStoryboardModalProps) {
  // 本地状态
  const [text2imageModelId, setText2imageModelId] = useState(initialConfig.text2imageModelId);
  const [imageEditModelId, setImageEditModelId] = useState(initialConfig.imageEditModelId);
  const [text2speechModelId, setText2speechModelId] = useState(initialConfig.text2speechModelId);
  const [aspectRatio, setAspectRatio] = useState(initialConfig.aspectRatio);
  const [generateImages, setGenerateImages] = useState(initialConfig.generateImages);
  const [generateEndFrame, setGenerateEndFrame] = useState(initialConfig.generateEndFrame);

  const { getModelById } = useModelStore();

  // 只在弹窗从关闭变为打开时重置为初始配置，避免父组件重渲染导致的意外重置
  const prevIsOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setText2imageModelId(initialConfig.text2imageModelId);
      setImageEditModelId(initialConfig.imageEditModelId);
      setText2speechModelId(initialConfig.text2speechModelId);
      setAspectRatio(initialConfig.aspectRatio);
      setGenerateImages(initialConfig.generateImages);
      setGenerateEndFrame(initialConfig.generateEndFrame);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]); // 不依赖 initialConfig，避免父组件重渲染触发重置

  // 计算预估剧点
  const estimatedCoins = useMemo(() => {
    if (!shotCount || shotCount === 0) return 0;

    const imageModel = text2imageModelId ? getModelById(text2imageModelId) : null;
    const audioModel = text2speechModelId ? getModelById(text2speechModelId) : null;

    // 计算分辨率倍率：1080p 双倍（仅图片部分）
    const resolutionMultiplier = resolution === Resolution.FHD_1080P ? 2 : 1;

    let total = 0;
    if (generateImages && imageModel?.coinPrice) {
      // 起始帧
      total += shotCount * imageModel.coinPrice * resolutionMultiplier;
      // 结束帧（仅当同时开启图片+尾帧时计入）
      if (generateEndFrame) {
        total += shotCount * imageModel.coinPrice * resolutionMultiplier;
      }
    }
    if (audioModel?.coinPrice) {
      total += shotCount * audioModel.coinPrice;
    }

    return total;
  }, [shotCount, text2imageModelId, text2speechModelId, resolution, generateImages, generateEndFrame, getModelById]);

  const handleConfirm = () => {
    onConfirm({
      text2imageModelId,
      imageEditModelId,
      text2speechModelId,
      aspectRatio,
      generateImages,
      // 关闭图片时强制关闭尾帧
      generateEndFrame: generateImages ? generateEndFrame : false,
    });
  };

  const title = isRegenerate ? '重新生成分镜' : '生成分镜';
  const description = isRegenerate
    ? `将根据修改后的剧本重新生成 ${shotCount} 个分镜，请确认以下配置：`
    : `即将生成 ${shotCount} 个分镜，请确认以下配置：`;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      {/* 背景遮罩 */}
      <div className="fixed inset-0 bg-black/60" aria-hidden="true" />

      {/* 弹窗容器 */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-lg bg-bg-page rounded-2xl border border-border-subtle shadow-2xl">
          {/* 头部 */}
          <div className="flex items-center justify-between p-4 border-b border-border-subtle">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <SparklesIcon className="w-5 h-5 text-primary" />
              </div>
              <DialogTitle className="text-base font-semibold text-gray-50">
                {title}
              </DialogTitle>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-200 hover:bg-bg-elevated rounded-lg transition-colors cursor-pointer"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          {/* 内容 */}
          <div className="p-4 space-y-5 max-h-[60vh] overflow-y-auto">
            <p className="text-sm text-gray-300">{description}</p>

            {/* 模型配置 - 只显示有多个选项的模型 */}
            {(text2imageModels.length > 1 || imageEditModels.length > 1 || text2speechModels.length > 1) && (
              <div className="space-y-3">
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider">
                  模型配置
                </label>
                <div className="grid grid-cols-1 gap-3">
                  {text2imageModels.length > 1 && (
                    <InlineModelSelector
                      label="文生图模型"
                      models={text2imageModels}
                      value={text2imageModelId}
                      onChange={setText2imageModelId}
                    />
                  )}
                  {imageEditModels.length > 1 && (
                    <InlineModelSelector
                      label="图片编辑模型"
                      models={imageEditModels}
                      value={imageEditModelId}
                      onChange={setImageEditModelId}
                    />
                  )}
                  {text2speechModels.length > 1 && (
                    <InlineModelSelector
                      label="配音模型"
                      models={text2speechModels}
                      value={text2speechModelId}
                      onChange={setText2speechModelId}
                    />
                  )}
                </div>
              </div>
            )}

            {/* 视频比例 */}
            <div className="space-y-3">
              <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider">
                视频比例
              </label>
              <AspectRatioButtons
                value={aspectRatio}
                onChange={setAspectRatio}
              />
            </div>

            {/* 高级选项 */}
            <div className="space-y-3">
              <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider">
                高级选项
              </label>
              <ToggleSwitch
                label="生成图片"
                description="为每个分镜生成起始帧。关闭后将跳过所有图片生成，仅处理音频等其他资源"
                checked={generateImages}
                onChange={(next) => {
                  setGenerateImages(next);
                  // 关闭图片时强制关闭尾帧，避免逻辑冲突
                  if (!next) setGenerateEndFrame(false);
                }}
              />
              <div className={generateImages ? '' : 'opacity-50 pointer-events-none'}>
                <ToggleSwitch
                  label="生成尾帧"
                  description="为每个分镜生成结束帧，用于视频过渡（需开启生成图片）"
                  checked={generateImages && generateEndFrame}
                  onChange={setGenerateEndFrame}
                />
              </div>
            </div>
          </div>

          {/* 底部按钮 */}
          <div className="flex gap-3 p-4 border-t border-border-subtle">
            <button
              onClick={onClose}
              disabled={isProcessing}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-300 bg-bg-elevated hover:bg-bg-surface rounded-lg transition-colors disabled:opacity-50 cursor-pointer"
            >
              取消
            </button>
            <button
              onClick={handleConfirm}
              disabled={isProcessing}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50 cursor-pointer"
            >
              {isProcessing ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  处理中...
                </>
              ) : (
                <>
                  <SparklesIcon className="w-4 h-4" />
                  确认{isRegenerate ? '重新生成' : '生成'}
                  {estimatedCoins > 0 && (
                    <span className="text-xs text-white/70 font-normal">· 约 {estimatedCoins} 剧点</span>
                  )}
                </>
              )}
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
